import { createEntityAdapter, createSelector, EntityState } from "@reduxjs/toolkit";

import { apiSlice } from "fond/api/apiSlice";
import { Store, TaskStatus, Workflow } from "fond/types";

import { multiProjectsSlice } from "./multiProjectsSlice";

type GetWorkflowsResponse = {
  Workflows: Workflow[];
};

export const workflowsAdaptor = createEntityAdapter<Workflow>({
  // Note that we are using the Project ID as the entity ID.
  // There can never be more than one workflow per project
  selectId: (entity: Workflow): string => entity.Version.Project.ID,
});
const workflowsInitialState = workflowsAdaptor.getInitialState();

export const workflowSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getWorkflows: build.query<EntityState<Workflow>, void>({
      query: () => "/v2/workflows",
      transformResponse: (response: GetWorkflowsResponse) => workflowsAdaptor.setAll(workflowsInitialState, response.Workflows),
    }),
  }),
});

export const { useGetWorkflowsQuery } = workflowSlice;

const selectWorkflowsResult = workflowSlice.endpoints.getWorkflows.select();
export const selectWorkflowsData = createSelector(selectWorkflowsResult, (workflowsResult) => workflowsResult.data);

export const {
  selectAll: selectAllWorkflows,
  selectById: selectWorkflowByProjectId,
  selectEntities: selectWorkflowEntities,
} = workflowsAdaptor.getSelectors((state: Store) => selectWorkflowsData(state) ?? workflowsInitialState);

/**
 * Determines the current total progress of subarea projects currently being created for
 * a MultiProject.
 *
 * Note that there are two different status checks required
 * 1) MultiProjectArea.ImportStatus indicates if the MultIProjectArea Project is currently being created.
 * 2) Workflow Status indicates if the MultiProjectArea Project is running a design.
 *
 * @returns An object containing the state of the workflow and the progress.
 *         `state` can be any of MultiProjectWorkflowState.
 *         `progress` is a number between 0 and 1 indicating the progress of the workflow.
 *
 * @example
 *         case 0: no projects are created yet
 *                returns { state: "NOT_STARTED", progress: null }
 *
 *         case 1: project 1 is complete
 *                 project 2 is complete
 *                 returns { state: "COMPLETE", progress: 1 }
 *
 *         case 2: project 1 is in error
 *                 project 2 is in error
 *                 returns { state: "ERROR", progress: null }
 *
 *         case 3: project 1 is complete
 *                 project 2 is in error
 *                 returns { state: "PARTIALLY_COMPLETE", progress: 1 }
 *
 *         case 4: project 1 is in progress with 0.2 progress
 *                 project 2 is in progress with 0.5 progress
 *                 returns { state: "IN_PROGRESS", progress: 0.35 }
 *                 * progress is calculated as (0.2+0.5)/2
 *
 *         case 5: project 1 is in error
 *                 project 2 is in progress with 0.5 progress
 *                 returns { state: "IN_PROGRESS", progress: 0.75 }
 *                 * progress is calculated as (1+0.5)/2
 *
 *         case 6: project 1 is complete
 *                 project 2 is in progress with 0.5 progress
 *                 returns { state: "PARTIALLY_COMPLETE", progress: 0.75 }
 *                 * progress is calculated as (1+0.5)/2
 *                 * Note the different returned state compared to case 4, this is because `PARTIALLY_COMPLETE`
 *                   indicates that at least one project is complete but not all(otherwise it would be `COMPLETE`).
 *                   It is allowed to create a MultiReport for MultiProjects in `PARTIALLY_COMPLETE` or `COMPLETE` state.
 *
 */
export const selectMultiProjectWorkflowStatus = createSelector(
  [selectWorkflowEntities, (state) => state, (_: Store, multiProjectId: string) => multiProjectId],
  (workflows, state: Store, multiProjectId) => {
    const multiProject = createSelector(multiProjectsSlice.endpoints.getMultiProject.select(multiProjectId), (result) => result.data)(state);

    // Create a map of project IDs to their import status
    const projectStatusMap: Record<string, TaskStatus | null> = {};
    multiProject?.Areas.forEach(({ Project, ImportStatus }) => {
      if (Project) {
        projectStatusMap[Project.ID] = ImportStatus;
      }
    });

    const projects = multiProject?.Areas.map(({ Project }) => Project).filter(Boolean);
    if (!projects || projects.length === 0 || Object.keys(workflows).length === 0) {
      return { state: "NOT_STARTED", progress: null };
    }

    // All projects are complete
    if (projects.every((project) => project && workflows[project.ID]?.Status?.State === "COMPLETE")) {
      return { state: "COMPLETE", progress: 1 };
    }

    // All projects are in error state(errors can happen in either the import or the design workflow, so need to check both)
    if (
      projects.every((project) => project && (workflows[project.ID]?.Status?.State === "ERROR" || projectStatusMap[project.ID]?.State === "ERROR"))
    ) {
      return { state: "ERROR", progress: null };
    }

    let progress = 0;
    let designState = "IN_PROGRESS";
    projects.forEach((project) => {
      if (project) {
        const status = workflows[project.ID]?.Status;
        if (workflows[project.ID]?.Status?.State === "ERROR" || (status?.State && ["COMPLETE", "ERROR"].includes(status?.State))) {
          // ERROR or COMPLETE state have null progress, but we treat them as 1 for progress calculation
          progress += 1;
          if (status?.State === "COMPLETE") {
            // Only set state to PARTIALLY_COMPLETE if we have at least one complete project
            designState = "PARTIALLY_COMPLETE";
          }
        } else {
          progress += status?.Progress || 0;
        }
      }
    });

    return { state: designState, progress: progress / projects.length };
  }
);
